/**
 * Конфигурацията на локалния резерв от облака — `GET /configuration/edge`
 * (§7/§8 на договора): включен ли е, LAN адресът на главната каса и в кой
 * режим облакът смята ресторанта. Чете се при вход и на всеки 5 минути
 * (графикът е в `edgeMode.ts`); последният отговор се кешира в
 * `localStorage["edge_config"]`, за да може приложението да тръгне БЕЗ
 * облак и пак да знае адреса на касата.
 *
 * `enabled: false` = нищо от резерва не е активно: няма огледало, няма
 * сонди, няма искане на разрешение за локалната мрежа.
 */
import { getEdgeConfiguration, type EdgeConfigurationOut } from "../../api/edge";
import {
  EDGE_CONFIG_STORAGE_KEY,
  edgeStorageGet,
  edgeStorageRemove,
  edgeStorageSet,
  type EdgeConfig,
} from "./edgeState";

/** `GET /configuration/edge` → вътрешната camelCase форма. */
export function toEdgeConfig(out: EdgeConfigurationOut): EdgeConfig {
  return {
    enabled: out.enabled === true,
    ip: out.ip ?? null,
    port: typeof out.port === "number" && out.port > 0 ? out.port : 8787,
    masterClientId: out.master_client_id ?? null,
    cloudMode: out.mode === "local" ? "local" : "cloud",
    edgeApi: typeof out.edge_api === "number" ? out.edge_api : 1,
  };
}

/**
 * Включен ли е резервът според КЕША (§14.6 №4 на договора). Кешът на
 * самоличността (`lib/userInfoCache.ts`) се ПИШЕ винаги, но се ХИДРАТИРА в
 * Redux само когато резервът е включен: при изключен резерв стартът трябва
 * да е точно както преди офлайн режима — ролите идват от облака и нищо не
 * може да „оживее" от миналото зареждане.
 */
export const isEdgeFallbackEnabledFromCache = (): boolean =>
  readCachedEdgeConfig()?.enabled === true;

export function readCachedEdgeConfig(): EdgeConfig | null {
  const raw = edgeStorageGet(EDGE_CONFIG_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<EdgeConfig>;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      enabled: parsed.enabled === true,
      ip: typeof parsed.ip === "string" ? parsed.ip : null,
      port: typeof parsed.port === "number" ? parsed.port : 8787,
      masterClientId:
        typeof parsed.masterClientId === "string" ? parsed.masterClientId : null,
      cloudMode: parsed.cloudMode === "local" ? "local" : "cloud",
      edgeApi: typeof parsed.edgeApi === "number" ? parsed.edgeApi : 1,
    };
  } catch {
    return null;
  }
}

export function writeCachedEdgeConfig(config: EdgeConfig | null) {
  if (!config) {
    edgeStorageRemove(EDGE_CONFIG_STORAGE_KEY);
    return;
  }
  edgeStorageSet(EDGE_CONFIG_STORAGE_KEY, JSON.stringify(config));
}

/**
 * Дърпа конфигурацията от облака и я кешира. null при провал (мрежа, 401,
 * стар BE без endpoint-а) — извикващият пази досегашната.
 */
export async function fetchEdgeConfig(): Promise<EdgeConfig | null> {
  try {
    const config = toEdgeConfig(await getEdgeConfiguration());
    writeCachedEdgeConfig(config);
    return config;
  } catch {
    return null;
  }
}
